import { ReactNode, useState } from 'react';
import { Add, ArrowBack, Close, Edit, Tune, WhatsApp } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaseResponseDto, ClientResponseDto } from '@bandido/shared/dtos';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { PaginationProps, ROWS_PER_PAGE_OPTIONS } from '@frontend/features/common/components/Table';
import { TablePagination } from '@frontend/features/common/components/Table/TablePagination';
import { useRememberedScroll } from '@frontend/features/common/hooks/useRememberedScroll';
import { useMobileViewport } from '@frontend/features/common/hooks/useMobileViewport';
import { dashboardReturnUrl } from '@frontend/features/dashboard/navigationMemory';
import { CaseMobileCard } from '@frontend/features/cases/components/CaseMobileCard';
import { useWhatsappClient } from '@frontend/features/whatsapp/components/WhatsappClientContext';
import { routes } from '@frontend/router/routes';

interface Props {
  client?: ClientResponseDto;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  cases: CaseResponseDto[];
  totalCases: number;
  casesLoading: boolean;
  casesFetching: boolean;
  casesPrevious: boolean;
  casesError: boolean;
  onRetryCases: () => void;
  onCreateCase: () => void;
  pagination: PaginationProps;
  search: string;
  onSearch: (value: string) => void;
  filters: ReactNode;
  editor: ReactNode;
}
export const ClientViewMobile = ({
  client,
  isLoading,
  isError,
  onRetry,
  cases,
  totalCases,
  casesLoading,
  casesFetching,
  casesPrevious,
  casesError,
  onRetryCases,
  onCreateCase,
  pagination,
  search,
  onSearch,
  filters,
  editor,
}: Props) => {
  const { t } = useTranslation();
  const { myProfile, ability } = useAuth();
  const { openWhatsapp } = useWhatsappClient();
  const [editing, setEditing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const viewport = useMobileViewport(editing);
  const scrollRef = useRememberedScroll(
    !isLoading && !casesLoading && !casesPrevious && !isError && !casesError,
    'client-detail',
  );
  const field = (label: string, value?: string | null) => (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {t(label)}
      </Typography>
      <Typography variant="body2">{value || t('common.unassigned')}</Typography>
    </Box>
  );
  return (
    <Box
      ref={scrollRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        p: 2,
        minWidth: 0,
        overflowWrap: 'anywhere',
        '& button': { minHeight: 44 },
        '& input, & .MuiSelect-select': { fontSize: 16 },
      }}
    >
      <Button
        component={RouterLink}
        startIcon={<ArrowBack />}
        to={dashboardReturnUrl(myProfile?.id, routes.dashboard.clients)}
        sx={{ mb: 1 }}
      >
        {t('clients.title')}
      </Button>
      {isError && (
        <Alert severity="error" action={<Button onClick={onRetry}>{t('common.refresh')}</Button>}>
          {t('clients.mobile.loadError')}
        </Alert>
      )}
      {isLoading ? (
        <Skeleton variant="rounded" height={180} />
      ) : (
        client && (
          <>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography component="h1" variant="h5" mb={1}>
                {[client.firstName, client.lastName].filter(Boolean).join(' ') || t('common.unassigned')}
              </Typography>
              <Typography variant="body2">{client.email}</Typography>
              <Typography variant="body2" color="text.secondary">
                {client.phoneNumber ? `+${client.phoneCountryCode || ''} ${client.phoneNumber}` : client.whatsappNumber}
              </Typography>
              <Stack direction="row" gap={1} mt={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  disabled={!client.whatsappNumber}
                  onClick={() => openWhatsapp(client.id)}
                >
                  WhatsApp
                </Button>
                {ability?.can('manage', 'Clients') && (
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditing(true)}>
                    {t('common.actions.edit')}
                  </Button>
                )}
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography component="h2" variant="h6" mb={2}>
                {t('clients.view.title')}
              </Typography>
              <Stack gap={2}>
                {field(
                  'clients.view.preferredAgent',
                  [client.preferredAgentFirstName, client.preferredAgentLastName].filter(Boolean).join(' '),
                )}
                {field('clients.view.agency', client.agencyName)}
                {client.organizationName && field('clients.table.organization', client.organizationName)}
              </Stack>
            </Paper>
            <Paper component="details" variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography component="summary" sx={{ minHeight: 44, cursor: 'pointer' }}>
                {t('clients.mobile.personalDetails')}
              </Typography>
              <Stack gap={2} pt={1}>
                {field(
                  'clients.table.location',
                  [client.city, client.state, client.country].filter(Boolean).join(', '),
                )}
                {field(
                  'clients.view.addressInfo',
                  [client.address1, client.address2, client.postalCode].filter(Boolean).join(', '),
                )}
                {field(
                  'clients.view.documentInfo',
                  [client.documentType, client.documentNumber].filter(Boolean).join(': '),
                )}
                {field('clients.view.whatsappNumber', client.whatsappNumber)}
                {field(
                  'clients.view.clientSince',
                  client.createdAt ? new Date(client.createdAt).toLocaleDateString() : '',
                )}
              </Stack>
            </Paper>
          </>
        )
      )}
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <Typography component="h2" variant="h6" flex={1}>
          {t('clients.mobile.associatedCases')} · {totalCases}
        </Typography>
        {ability?.can('manage', 'Cases') && (
          <Button startIcon={<Add />} onClick={onCreateCase} disabled={!client}>
            {t('common.new')}
          </Button>
        )}
      </Stack>
      <Box mb={1} sx={{ '& .MuiTextField-root': { maxWidth: 'none' } }}>
        <SearchInput value={search} onSearch={onSearch} placeholder={t('cases.list.search')} />
      </Box>
      <Button variant="outlined" startIcon={<Tune />} onClick={() => setFiltersOpen(true)} sx={{ mb: 2 }}>
        {t('common.filters')}
      </Button>
      {casesFetching && !casesLoading && <LinearProgress aria-label={t('cases.list.refreshing')} />}
      {casesError && (
        <Alert severity="error" action={<Button onClick={onRetryCases}>{t('common.refresh')}</Button>}>
          {t('cases.list.error')}
        </Alert>
      )}
      {casesLoading ? (
        <Skeleton variant="rounded" height={160} />
      ) : (
        <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
          {cases.map((item) => (
            <CaseMobileCard key={item.id} item={item} />
          ))}
        </List>
      )}
      {!casesLoading && !casesError && !cases.length && (
        <Typography textAlign="center" py={3}>
          {t('common.noData')}
        </Typography>
      )}
      <TablePagination
        compact
        totalRows={totalCases}
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onChangePage={pagination.handlePagination}
        onChangeRowsPerPage={pagination.handleRowsPerPage}
      />
      <Drawer
        anchor="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            maxHeight: '90dvh',
            p: 2,
            borderRadius: '16px 16px 0 0',
            pb: 'max(16px, env(safe-area-inset-bottom))',
            '& input': { fontSize: 16 },
            '& button': { minHeight: 44 },
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{t('common.filters')}</Typography>
          <IconButton aria-label={t('common.close')} onClick={() => setFiltersOpen(false)}>
            <Close />
          </IconButton>
        </Stack>
        {filters}
        <Button variant="contained" sx={{ mt: 2, minHeight: 44 }} onClick={() => setFiltersOpen(false)}>
          {t('common.mobileList.showResults')}
        </Button>
      </Drawer>
      <Dialog
        fullScreen
        open={editing}
        onClose={() => setEditing(false)}
        PaperProps={{
          sx: {
            position: 'fixed',
            ...viewport,
            maxHeight: 'none',
            pt: 'env(safe-area-inset-top)',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => setEditing(false)}
          sx={{ alignSelf: 'flex-start', minHeight: 44, flexShrink: 0, mx: 2 }}
        >
          {t('clients.mobile.backToClient')}
        </Button>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            '& > .MuiCard-root': { p: 2 },
            '& input, & .MuiSelect-select': { fontSize: 16 },
            '& button': { minHeight: 44 },
            '& .client-form-row': { flexDirection: 'column' },
          }}
        >
          {editor}
        </Box>
      </Dialog>
    </Box>
  );
};
